import * as React from 'react';
import { CSSProperties } from 'react';
import { observer } from 'mobx-react';
import SelectorLabel from '@/elements/shared/SelectorLabel/SelectorLabel';
import { Card } from '@/elements/shared/Card/Card';
import Tooltip from '@/ui-kit/components/Tooltip/Tooltip';
import { SelectOptionAction } from '@/ui-kit/components/SelectOptionAction/SelectOptionAction';

import { useBootstrapper } from '@/ui-kit/contexts/BootstrapperContext';
import { RalAttributeState } from './RalAttributeState';
import { OptionsViewStateName } from '@/stores/OptionsConfiguratorUIStore';
import { Icon, Typography, useUiLabel } from '@canvas-logic/clp-shared-ui';

import { AttributeCfgUIElement } from '@/elements/attributes/AttributeLayout';
import { useAttributeTranslation } from '@/localization/useAttributeTranslation';
import classNames from 'classnames';

interface RalViewProps {
  state: RalAttributeState;
}

export const RalAttributeCfgUIElement = observer(function RalAttributeCfgUIElement({ state }: RalViewProps) {
  const selectButtonLabel = useUiLabel('ui.colorPicker.selectButton');
  const isCustomColorSelected = !!state.color && state.color.custom;
  const { uiControl } = state.viewElement;
  const customColorControl = uiControl.customColorOptions;
  // `optionsViewStore` is populated in the Bootstrapper constructor, so it is already
  // available on `earlyBootstrapper` — the only instance exposed while the 3D scene is
  // still loading and the panel is already interactive.
  const { earlyBootstrapper } = useBootstrapper();
  const cardOrientation = uiControl.cardType === 'grid' ? 'vertical' : 'horizontal';
  const tAttr = useAttributeTranslation(state.componentKey, state.attributeKey);

  return (
    <AttributeCfgUIElement state={state}>
      <div>
        <ul
          className={classNames(
            'cp-attribute__options',
            uiControl.cardType === 'grid' && 'cp-attribute__options--card',
          )}
        >
          {state.availableColors.map((color) => {
            const isSelected = state.color?.value.ral === color.ral;
            const text = state.textData.values.find((it) => it.value === color.ral);
            const hasTooltip = uiControl.itemOptions?.showLongDescription && !!text?.description;
            const displayLabel = tAttr.value(color.ral, text?.label || color.ral);
            const shouldShowImage = uiControl.itemOptions?.showImage !== false;
            const imageSrc = shouldShowImage ? text?.image?.url || undefined : undefined;

            return (
              <li key={color.ral} className={'cp-attribute__option'}>
                <Tooltip disabled={!hasTooltip} title={displayLabel} description={text?.description}>
                  {(onOpen) => (
                    <Card
                      orientation={cardOrientation}
                      value={color.ral}
                      onSelect={state.setColor}
                      selected={isSelected}
                      label={uiControl.itemOptions?.showTitle ? displayLabel : ''}
                      imageType="icon"
                      imageSrc={imageSrc}
                      imageFit={uiControl.itemOptions?.imageFit}
                      icon={shouldShowImage && !imageSrc ? <ColorCell color={color.hex} /> : undefined}
                      description={uiControl.itemOptions?.showDescription ? text?.description : ''}
                      hasTooltip={hasTooltip}
                      onTooltipClick={onOpen}
                    />
                  )}
                </Tooltip>
              </li>
            );
          })}
        </ul>
      </div>
      {state.hasCustom && (
        <div className="cp-ral-attribute__custom">
          <SelectorLabel
            className="cp-ral-attribute__text"
            component="div"
            title={customColorControl?.showTitle ? customColorControl.title : ''}
            description={''}
          />
          <SelectOptionAction
            selected={isCustomColorSelected}
            onClick={() =>
              earlyBootstrapper?.optionsViewStore.setView({
                name: OptionsViewStateName.SELECT_CUSTOM_COLOR,
                state,
              })
            }
          >
            {isCustomColorSelected ? (
              <div className="cp-ral-attribute__action-start-elem">
                {uiControl.customColorOptions?.showImage && (
                  <ColorCell width={80} height={80} color={state.color?.value.hex ?? ''} />
                )}
                {uiControl.customColorOptions?.showTitle && (
                  <Typography component="div" variant="button">
                    {state.color?.value.ral}
                  </Typography>
                )}
              </div>
            ) : (
              <div className="cp-ral-attribute__action-start-elem">
                <Icon type="palette" colored />
                <div>{uiControl.customColorOptions?.title || selectButtonLabel}</div>
              </div>
            )}
          </SelectOptionAction>
        </div>
      )}
    </AttributeCfgUIElement>
  );
});

export const ColorCell = ({ color, width, height }: { color: string; width?: number; height?: number }) => {
  const cellStyle = {
    '--color-cell-value': color,
    ...(width && { '--color-cell-width': `${width}px` }),
    ...(height && { '--color-cell-height': `${height}px` }),
  } as CSSProperties;

  return <div style={cellStyle} className="cp-ral-attribute__color-cell" />;
};
